import {
  useRef,
  useState,
  forwardRef,
  useCallback,
  useLayoutEffect,
  PropsWithChildren,
} from "react"
import { Box, Typography } from "@mui/material"
import type { FC } from ".."
import { Link } from ".."

const NotFoundPage: FC = () => {
  const [navHeight, setNavHeight] = useState(62)
  const ref = useRef<HTMLDivElement>(null)
  const getNavHeight = useCallback(() => {
    if (ref.current && ref.current.previousElementSibling) {
      setNavHeight(ref.current.previousElementSibling.clientHeight)
      ref.current.style.marginTop = `${ref.current.previousElementSibling.clientHeight * -1}px`
    }
  }, [ref.current])

  useLayoutEffect(() => {
    getNavHeight()
  }, [ref.current])

  return (
    <PageContainer ref={ref} navHeight={navHeight}>
      <Typography
        variant="heading.3xl"
        sx={{
          fontSize: { xs: "84px", md: "184px" },
          fontWeight: 800,
          color: (theme) => theme.palette["secondary.800"].main,
          lineHeight: "70%",
        }}
      >
        404
      </Typography>
      <Typography variant="body.lg" sx={{ color: (theme) => theme.palette["secondary.800"].main }}>
        It looks like this page doesn&#39;t exist, try returning to our{" "}
        <Link
          type="underline"
          variant="body.lg"
          href="/"
          sx={{
            "color": (theme) => theme.palette["secondary.800"].main,
            "background": (theme) =>
              `linear-gradient(to right, ${theme.palette["secondary.800"].main}, ${theme.palette["secondary.800"].main})`,
            "backgroundSize": "100% 1px",
            "backgroundPosition": "0 100%",
            "backgroundRepeat": "no-repeat",
            "pb": 0.5,
            "transition": "background-size 400ms",

            "&:hover": {
              backgroundSize: "0 1px",
            },
          }}
        >
          homepage
        </Link>
        .
      </Typography>
    </PageContainer>
  )
}
export default NotFoundPage

/** A sub-component for the container */
const PageContainer = forwardRef<HTMLDivElement, PropsWithChildren & { navHeight: number }>(
  ({ children, navHeight }, ref) => (
    <Box
      ref={ref}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap={12.5}
      height={{ xs: `calc(100vh - ${navHeight}px)`, lg: "110vh" }}
      borderRadius={1.5}
      px={10}
      m={3}
      bgcolor={(theme) => theme.palette["secondary.200"].main}
      textAlign="center"
    >
      {children}
    </Box>
  )
)

PageContainer.displayName = "PageContainer"
